import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, Loader2, MessageCircle } from 'lucide-react';
import { client } from '../../api/client';
import { localizedName, localizedDescription } from '../../lib/localized';
import { MOOD_EXPERIENCE_MAP, MOOD_LABELS } from '../../config/experienceMoodMap';

interface ApiExperience {
  id: number;
  name: string;
  nameEn?: string | null;
  nameZh?: string | null;
  nameZhCn?: string | null;
  slug: string;
  description: string;
  descriptionEn?: string | null;
  descriptionZh?: string | null;
  descriptionZhCn?: string | null;
  duration: string;
  groupSize: string;
  imageUrl: string;
  price: number | null;
  priceNote: string;
  status: string;
}

interface DisplayExperience {
  id: number;
  slug: string;
  name: string;
  description: string;
  imageUrl: string;
}

const WHATSAPP_NUMBER = '85262322466';
const DELAY_CLASSES = ['', 'fade-in-up-delay-1', 'fade-in-up-delay-2', 'fade-in-up-delay-3'];

function mapApiExperience(item: ApiExperience): DisplayExperience {
  return {
    id: item.id,
    slug: item.slug,
    name: localizedName(item),
    description: localizedDescription(item),
    imageUrl: item.imageUrl,
  };
}

const MOOD_ORDER = ['couple', 'family', 'friends', 'adventure', 'relax'];

export default function ExperienceSelector() {
  const { t } = useTranslation('home');
  const [experiences, setExperiences] = useState<DisplayExperience[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMoods, setSelectedMoods] = useState<string[]>([]);
  const [selectedSlugs, setSelectedSlugs] = useState<string[]>([]);

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        const res = await client.api.fetch('/api/public/experiences');
        if (!res.ok) {
          throw new Error(`Experiences API error ${res.status}`);
        }
        const json = (await res.json()) as { data?: ApiExperience[] };
        const list = Array.isArray(json?.data)
          ? json.data.filter((e) => e.status === 'active').map(mapApiExperience)
          : [];
        if (mounted) {
          setExperiences(list);
        }
      } catch (err) {
        console.error('ExperienceSelector load error:', err);
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Unknown error');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, []);

  const defaultExperiences = useMemo(() => {
    if (experiences.length <= 3) return experiences;
    const shuffled = [...experiences].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 3);
  }, [experiences]);

  const matchedExperiences = useMemo(() => {
    if (selectedMoods.length === 0) return defaultExperiences;
    const ids = new Set<number>();
    selectedMoods.forEach((mood) => {
      MOOD_EXPERIENCE_MAP[mood]?.forEach((id) => ids.add(id));
    });
    return experiences.filter((e) => ids.has(e.id)).slice(0, 3);
  }, [selectedMoods, experiences, defaultExperiences]);

  const selectedExperiences = useMemo(
    () => experiences.filter((e) => selectedSlugs.includes(e.slug)),
    [experiences, selectedSlugs]
  );

  const listKey = selectedMoods.length === 0
    ? 'default'
    : selectedMoods.slice().sort().join(',');

  const toggleMood = (mood: string) => {
    setSelectedMoods((prev) =>
      prev.includes(mood) ? prev.filter((m) => m !== mood) : [...prev, mood]
    );
  };

  const toggleExperience = (slug: string) => {
    setSelectedSlugs((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]
    );
  };

  const handleWhatsAppClick = () => {
    let text: string;
    if (selectedExperiences.length === 0) {
      text = t('experienceSelector.whatsapp.empty');
    } else {
      const list = selectedExperiences.map((e) => `• ${e.name}`).join('\n');
      text = t('experienceSelector.whatsapp.withSelection', { list });
    }
    window.location.href = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;
  };

  return (
    <section className="bg-[#0d1b2a] py-16 md:py-24 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10 md:mb-12">
          <p className="text-[#B8902F] font-medium mb-2 tracking-widest uppercase text-sm">
            {t('experienceSelector.header.eyebrow')}
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {t('experienceSelector.header.title')}
          </h2>
          <p className="text-white/70 max-w-2xl mx-auto">
            {t('experienceSelector.header.description')}
          </p>
        </div>

        {/* Mood chips */}
        <div className="flex overflow-x-auto scrollbar-hide gap-3 justify-start md:justify-center pb-4 mb-10 md:mb-12">
          {MOOD_LABELS.map((mood, i) => {
            const active = selectedMoods.includes(mood);
            return (
              <button
                key={mood}
                type="button"
                onClick={() => toggleMood(mood)}
                aria-pressed={active}
                className={`flex-shrink-0 px-5 py-2.5 rounded-full text-sm font-medium transition border ${
                  active
                    ? 'bg-[#B8902F] border-[#B8902F] text-white'
                    : 'bg-transparent border-white/30 text-white hover:bg-white/10'
                }`}
              >
                {t(`experienceSelector.moods.${MOOD_ORDER[i]}`)}
              </button>
            );
          })}
        </div>

        {/* Cards */}
        {loading ? (
          <div className="flex md:grid md:grid-cols-3 gap-6 overflow-x-auto md:overflow-visible pb-4 md:pb-0 scrollbar-hide">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="min-w-[280px] md:min-w-0 flex-1 bg-white/5 border border-white/10 rounded-2xl overflow-hidden animate-pulse"
              >
                <div className="aspect-[4/3] bg-white/10" />
                <div className="p-5 space-y-3">
                  <div className="h-5 bg-white/10 rounded w-2/3" />
                  <div className="h-4 bg-white/10 rounded w-full" />
                  <div className="h-4 bg-white/10 rounded w-5/6" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-white/70 mb-2">{t('experienceSelector.error.message')}</p>
            <p className="text-sm text-white/40">{error}</p>
          </div>
        ) : matchedExperiences.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-white/70">{t('experienceSelector.empty')}</p>
          </div>
        ) : (
          <div
            key={listKey}
            className="flex md:grid md:grid-cols-3 gap-6 overflow-x-auto md:overflow-visible pb-4 md:pb-0 snap-x scrollbar-hide"
          >
            {matchedExperiences.map((exp, idx) => {
              const isSelected = selectedSlugs.includes(exp.slug);
              const delayClass = DELAY_CLASSES[idx + 1] || '';
              return (
                <div
                  key={exp.slug}
                  className={`min-w-[280px] md:min-w-0 flex-1 bg-white/5 border border-white/10 rounded-2xl overflow-hidden backdrop-blur-sm flex flex-col fade-in-up ${delayClass}`}
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={exp.imageUrl}
                      alt={exp.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0d1b2a]/60 to-transparent" />
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="text-lg font-bold text-white mb-2">{exp.name}</h3>
                    <p className="text-white/70 text-sm leading-relaxed line-clamp-2 flex-1">
                      {exp.description}
                    </p>
                    <button
                      type="button"
                      onClick={() => toggleExperience(exp.slug)}
                      className={`mt-4 w-full py-2.5 rounded-full text-sm font-medium transition flex items-center justify-center gap-2 border ${
                        isSelected
                          ? 'bg-[#B8902F] border-[#B8902F] text-white'
                          : 'bg-transparent border-white/30 text-white hover:bg-white/10'
                      }`}
                    >
                      {isSelected ? (
                        <>
                          <Check className="w-4 h-4" />
                          {t('experienceSelector.card.selected')}
                        </>
                      ) : (
                        <>{t('experienceSelector.card.add')}</>
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Bottom CTA bar */}
        <div className="mt-10 md:mt-12 bg-white/5 border border-white/10 rounded-2xl p-5 md:p-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-center md:text-left">
            <p className="text-white font-medium">
              {selectedExperiences.length === 0
                ? t('experienceSelector.selection.none')
                : t('experienceSelector.selection.count', { count: selectedExperiences.length })}
            </p>
            <p className="text-white/50 text-sm">
              {selectedExperiences.length === 0
                ? t('experienceSelector.selection.hint')
                : selectedExperiences.map((e) => e.name).join('、')}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            <Link
              to="/plan"
              className="inline-flex items-center justify-center px-6 py-3 bg-[#B8902F] text-white rounded-full font-semibold hover:bg-[#9a7a28] transition"
            >
              {t('experienceSelector.cta.plan')} →
            </Link>
            <button
              type="button"
              onClick={handleWhatsAppClick}
              className="inline-flex items-center justify-center px-6 py-3 bg-transparent border border-white/30 text-white rounded-full font-semibold hover:bg-white/10 transition"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              {t('experienceSelector.cta.whatsapp')}
            </button>
          </div>
        </div>

        {/* View all link */}
        <div className="text-center mt-8">
          <Link
            to="/experiences"
            className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm transition"
          >
            {t('experienceSelector.cta.viewAll')}
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
}
